import { FormControl, FormHelperText, FormLabel, TextField } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Autocomplete, AutocompleteRenderOptionState } from '@material-ui/lab';
import { useField } from 'formik';
import React from 'react';
import useCommonStyles from '../../useCommonStyles';

export interface OutlinedMultiSelectFieldProps<Option> {
    name: string;
    label?: string;
    placeholder?: string;
    helperText?: string;
    options: Option[];
    getOptionLabel: (option: Option) => string;
    getOptionId?: (option: Option) => string;
    onSelectionChange?: (selectedIds: string[]) => void;
    noOptionsText?: string;
    color?: 'primary' | 'secondary';
    renderOption?: (option: Option, state: AutocompleteRenderOptionState) => React.ReactNode;
}

const useStyles = makeStyles((theme: Theme) => ({
    // MUI only guarantees a 48px option above the sm breakpoint, below it the options fall
    // under the minimum comfortable touch target
    option: {
        minHeight: 48,
    },
    // Autocomplete defaults its popup to the modal layer, which floats it over the fixed app bar
    // and bottom navigation. Below the sm breakpoint it is dropped under the lower of the two
    // (they sit on appBar and drawer + 1) so the dropdown is clipped behind the bars as the page
    // scrolls. Above that breakpoint the bars do not overlay the content and the popup keeps the
    // default, so a dropdown inside a dialog still renders above the dialog.
    popper: {
        [theme.breakpoints.down('sm')]: {
            zIndex: theme.zIndex.appBar - 1,
        },
    },
}));

const OutlinedMultiSelectField = <Option,>({
    name,
    label,
    placeholder,
    helperText,
    options,
    getOptionLabel,
    getOptionId = option => (option as unknown as { id: string }).id,
    onSelectionChange,
    noOptionsText,
    color,
    renderOption,
}: OutlinedMultiSelectFieldProps<Option>) => {
    const [{ value }, meta, { setValue }] = useField<string[]>(name);
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    const hasError = !!meta.error && meta.touched;

    const selectedOptions = (value ?? [])
        .map(id => options.find(option => getOptionId(option) === id))
        .filter((option): option is Option => !!option);

    return (
        <FormControl variant="outlined" fullWidth>
            {label && <FormLabel className={commonStyles.formLabel}>{label}</FormLabel>}
            <Autocomplete<Option, true, undefined, false>
                ListboxProps={{ style: { maxHeight: '40vh' } }}
                classes={{ option: styles.option, popper: styles.popper }}
                getOptionLabel={getOptionLabel}
                noOptionsText={noOptionsText}
                onChange={(_event, selected) => {
                    const selectedIds = selected.map(getOptionId);

                    if (onSelectionChange) {
                        onSelectionChange(selectedIds);
                    } else {
                        setValue(selectedIds);
                    }
                }}
                options={options}
                renderInput={params => (
                    <TextField
                        {...params}
                        color={color}
                        error={hasError}
                        inputProps={{
                            ...params.inputProps,
                        }}
                        placeholder={placeholder}
                        variant="outlined"
                    />
                )}
                renderOption={renderOption}
                value={selectedOptions}
                multiple
            />
            {helperText && <FormHelperText style={{ marginLeft: '0px' }}>{helperText}</FormHelperText>}
            <FormHelperText error={hasError}>{hasError ? meta.error : ''}</FormHelperText>
        </FormControl>
    );
};

export default OutlinedMultiSelectField;
